import { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { fmtMoney, fmtDate, Badge, PAYMENT_METHODS } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { DataTable } from "../components/DataTable";
import { Modal, Field, inputCls, selectCls, btnPrimary, btnSecondary } from "../components/Modal";
import { toast } from "sonner";
import { Plus, PackageCheck, Coins, Trash2 } from "lucide-react";
import { purchasePriceFor } from "../lib/units";

export default function Purchases() {
  const { user, hasPerm } = useAuth();
  const cur = user?.currency || "FCFA";
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [open, setOpen] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const [pay, setPay] = useState({ method: "Espèces", amount: "" });
  const [form, setForm] = useState({ supplier_id: "", warehouse_id: "", transport_cost: "", other_costs: "", paid: "", method: "Espèces", supplier_invoice: "", items: [] });

  const load = () => api.get("/purchases").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    api.get("/products", { params: { status: "active", limit: 2000 } }).then((r) => setProducts(r.data.items)).catch(() => {});
    api.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
  }, []);

  const itemsTotal = form.items.reduce((s, i) => s + i.qty * i.unit_price, 0);
  const total = itemsTotal + Number(form.transport_cost || 0) + Number(form.other_costs || 0);

  const addItem = () => setForm({ ...form, items: [...form.items, { _k: crypto.randomUUID(), product_id: "", qty: 1, unit: "", unit_price: "" }] });
  const setItem = (i, k, v) => {
    const items = form.items.map((x, j) => {
      if (j !== i) return x;
      const n = { ...x, [k]: v };
      if (k === "product_id") {
        const p = products.find((pp) => pp.id === v);
        n.unit = "";
        n.unit_price = p && p.purchase_price ? p.purchase_price : "";
      }
      if (k === "unit") {
        const p = products.find((pp) => pp.id === x.product_id);
        if (p) n.unit_price = purchasePriceFor(p, v) || "";
      }
      return n;
    });
    setForm({ ...form, items });
  };

  const save = async () => {
    try {
      await api.post("/purchases", { ...form, transport_cost: Number(form.transport_cost) || 0, other_costs: Number(form.other_costs) || 0, paid: Number(form.paid) || 0,
        items: form.items.map((i) => ({ product_id: i.product_id, qty: Number(i.qty), unit: i.unit || "", unit_price: Number(i.unit_price) })) });
      toast.success("Bon de commande créé");
      setOpen(false);
      setForm({ supplier_id: "", warehouse_id: "", transport_cost: "", other_costs: "", paid: "", method: "Espèces", supplier_invoice: "", items: [] });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const receive = async (p) => {
    try {
      const r = await api.post(`/purchases/${p.id}/receive`, {});
      toast.success(r.data.status === "received" ? "Réception complète — stock mis à jour" : "Réception partielle enregistrée");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const doPay = async () => {
    try {
      await api.post(`/purchases/${payFor.id}/payments`, { method: pay.method, amount: Number(pay.amount) });
      toast.success("Règlement fournisseur enregistré");
      setPayFor(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const columns = [
    { key: "number", label: "N°", render: (p) => <span className="font-money font-bold">{p.number}</span> },
    { key: "date", label: "Date", render: (p) => fmtDate(p.date) },
    { key: "supplier_name", label: "Fournisseur" },
    { key: "items", label: "Articles", render: (p) => <span className="text-xs">{p.items.map((i) => `${i.name} ×${i.qty}`).join(", ")}</span> },
    { key: "total", label: "Total", className: "text-right", render: (p) => <span className="font-money font-bold">{fmtMoney(p.total, cur)}</span> },
    { key: "balance", label: "Reste", className: "text-right", render: (p) => <span className={`font-money ${p.balance > 0 ? "text-rose-600 font-bold" : ""}`}>{fmtMoney(p.balance, cur)}</span> },
    { key: "status", label: "Réception", render: (p) => <Badge status={p.status === "partial" ? "partial" : p.status} /> },
    { key: "actions", label: "", className: "text-right", render: (p) => (
      <div className="flex justify-end gap-1">
        {hasPerm("purchases.receive") && p.status !== "received" && <button data-testid={`receive-${p.id}`} onClick={() => receive(p)} title="Réceptionner" className="h-8 w-8 inline-flex items-center justify-center rounded-md text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950"><PackageCheck className="h-4 w-4" /></button>}
        {p.balance > 0 && <button data-testid={`pay-${p.id}`} onClick={() => { setPayFor(p); setPay({ method: "Espèces", amount: String(p.balance) }); }} title="Régler" className="h-8 w-8 inline-flex items-center justify-center rounded-md text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950"><Coins className="h-4 w-4" /></button>}
      </div>) },
  ];

  return (
    <div className="space-y-4" data-testid="purchases-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Achats & Réceptions</h1>
        {hasPerm("purchases.create") && <button data-testid="add-purchase-btn" onClick={() => setOpen(true)} className={btnPrimary}><Plus className="h-4 w-4" /> Nouvel achat</button>}
      </div>
      <DataTable testid="purchases-table" columns={columns} rows={rows} searchKeys={["number", "supplier_name"]} pageSize={15} />

      <Modal open={open} onClose={() => setOpen(false)} title="Nouveau bon de commande" wide>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Fournisseur *">
            <select data-testid="po-supplier" value={form.supplier_id} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })} className={selectCls}>
              <option value="">— Choisir —</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Dépôt de réception *">
            <select data-testid="po-warehouse" value={form.warehouse_id} onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })} className={selectCls}>
              <option value="">— Choisir —</option>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </Field>
        </div>
        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Articles</span>
            <button data-testid="po-add-item" onClick={addItem} className="text-xs text-primary font-semibold hover:underline">+ Article</button>
          </div>
          {form.items.map((it, i) => {
            const p = products.find((pp) => pp.id === it.product_id);
            return (
            <div key={it._k || i} className="flex gap-2 items-center flex-wrap">
              <select data-testid={`po-item-${i}`} value={it.product_id} onChange={(e) => setItem(i, "product_id", e.target.value)} className={`${selectCls} flex-1`}>
                <option value="">— Produit —</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              {(p?.units || []).length > 0 && (
                <select data-testid={`po-unit-${i}`} value={it.unit || ""} onChange={(e) => setItem(i, "unit", e.target.value)} className={`${selectCls} w-32`}>
                  <option value="">{p.unit}</option>
                  {p.units.map((u) => <option key={u.unit} value={u.unit}>{u.unit} (×{u.factor})</option>)}
                </select>
              )}
              <input data-testid={`po-qty-${i}`} type="number" min="0.01" step="any" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} className={`${inputCls} w-20`} placeholder="Qté" />
              <input data-testid={`po-price-${i}`} type="number" min="0" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} className={`${inputCls} w-28`} placeholder="P.U." />
              <button onClick={() => setForm({ ...form, items: form.items.filter((_, j) => j !== i) })} className="text-rose-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          ); })}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
          <Field label="Transport"><input data-testid="po-transport" type="number" min="0" value={form.transport_cost} onChange={(e) => setForm({ ...form, transport_cost: e.target.value })} className={inputCls} /></Field>
          <Field label="Autres frais"><input data-testid="po-other" type="number" min="0" value={form.other_costs} onChange={(e) => setForm({ ...form, other_costs: e.target.value })} className={inputCls} /></Field>
          <Field label="Montant payé"><input data-testid="po-paid" type="number" min="0" value={form.paid} onChange={(e) => setForm({ ...form, paid: e.target.value })} className={inputCls} /></Field>
          <Field label="Mode">
            <select data-testid="po-method" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className={selectCls}>
              {PAYMENT_METHODS.map((m) => <option key={m}>{m}</option>)}
            </select>
          </Field>
        </div>
        <Field label="N° facture fournisseur"><input data-testid="po-invoice" value={form.supplier_invoice} onChange={(e) => setForm({ ...form, supplier_invoice: e.target.value })} className={inputCls} /></Field>
        <div className="flex items-center justify-between font-heading font-bold text-lg mt-3">
          <span>Total</span><span data-testid="po-total" className="font-money text-primary">{fmtMoney(total, cur)}</span>
        </div>
        <button data-testid="po-submit" onClick={save} disabled={!form.supplier_id || !form.warehouse_id || form.items.length === 0 || form.items.some((i) => !i.product_id)} className={`${btnPrimary} w-full mt-2`}>Créer le bon de commande</button>
      </Modal>

      <Modal open={!!payFor} onClose={() => setPayFor(null)} title={`Régler — ${payFor?.number}`}>
        <div className="space-y-3">
          <p className="text-sm">Reste dû à <b>{payFor?.supplier_name}</b> : <b className="font-money">{fmtMoney(payFor?.balance, cur)}</b></p>
          <Field label="Mode">
            <select data-testid="po-pay-method" value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} className={selectCls}>
              {PAYMENT_METHODS.map((m) => <option key={m}>{m}</option>)}
            </select>
          </Field>
          <Field label="Montant"><input data-testid="po-pay-amount" type="number" min="0" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className={inputCls} /></Field>
          <button data-testid="po-pay-confirm" onClick={doPay} disabled={!Number(pay.amount)} className={`${btnPrimary} w-full`}>Enregistrer</button>
        </div>
      </Modal>
    </div>
  );
}
